import { reactive, createApp } from 'vue'
import { PartOption, Upsell } from './'
import { getSkuWithoutLensTech } from '~/assets/js/helpers/product'
import allLayerTemplates from '~/assets/360/all.mjs'
import { TAG_EYEGLASSES, TAG_PRESCRIPTION } from '~/assets/js/constants/commerceTags'
import { getFreshStockQuantities } from '~/assets/js/helpers/shopify'
import dayjs from 'dayjs'

const PRODUCT_FIELDS = [
  'id',
  'commerceId',
  'sku',
  'name',
  'basePrice',
  'basePriceGBP',
  'doNotTrackStock',
  'allowPreorders',
  'availableForSale',
  'quantity',
  'locationInventory',
  'inboundQuantity',
  'inboundDeliveryDate',
  'preorderAsDelayedDespatch',
  'variantGroup',
  'despatchDelayedUntil',
  'currency',
  'subTitle',
  'url',
  'hasPdp',
  'pageMode',
  'icon',
  'swappableParts',
  'frameTypeOverride',
  'useBaseProductName',
  'lensNameOverride',
  'limitedEditionLogo',
  'logoWidth',
  'hideLensTech',
  'swatchIconOverride',
  'swatchCornerIcon',
  'swatchCornerStyle',
  'showPackagingOptions',
  'defaultPackagingOption',
  'staticLensTech',
  'productHeroDesigns',
  'lineItemContent',
  'showReviews',
  'useGenericReviews',
  '_allReferencingProductReviewAggregations',
  'tagText',
  'tagTheme',
  'includeInProductFeed',
  'partsLimitedEdition',
  'productInfoTitle',
  'promotionProductGroup',
  'promoMessaging',
  'relatedProducts',
  'priceGBP',
  'shopifyPrebuiltProduct',
  'directCartUpsells',
  'productFamily',
]

const PDP_FIELDS = [
  'preTitle',
  'designImagesOrder',
  'buttonToolTipTitle',
  'buttonToolTipContent',
  'hideSwatches',
  'stockIndicatorThreshold',
  'stockIndicatorThresholdText',
  'stockIndicatorSoldOutText',
  'inTheBoxItems',
  'showDefaultInTheBoxProduct',
  'quotes',
  'quotesSectionTitle',
  'techSpecs',
  'lensGuideBanner',
  'lensPreviewScenes',
  'showLensPreviewIcons',
  'lensPreviewConditions',
  'secondaryProduct',
  'meta',
  'limitedEditions',
  'secondaryCtaLink',
  'secondaryCtaLinkText',
  'productInfoTitle',
  'productInfoText',
  'pdpText',
  'theme',
  'lifestyleImages',
  'lifestyleImagesOverride',
  'ctaSuffix',
  'requireSubscriptionToPurchase',
  'showRegisterInterestForm',
  'registerInterestFormCopy',
  'staticProductImages',
  'showProductInfoTextBlock',
  'sampleReview',
  'convincerBlocks',
  'frameSpecification',
  'lensShape',
  'lensChange',
  'weight',
  'armLength',
  'heightFrame',
  'lengthFrame',
  'heightLenses',
  'widthLenses',
  'vlt',
  'technicalImageHero',
  'technicalImageFront',
  'technicalImageSide',
  'showReviewsForProductOverride',
  'showPercentageOff',
  'percentageOffTagTheme',
  'hideCustomiser',
]

export default class Product {
  constructor(data, includePDPConfig) {
    this._rawData = data

    PRODUCT_FIELDS.forEach((field) => {
      this[field] = data[field]
    })
    this.compareAtPrice = data.compare_at_amount_float

    // Store PDP blocks if they've been fetched
    if (includePDPConfig) {
      this.pdpLoaded = true
      this.pdpConfig = {}
      PDP_FIELDS.forEach((field) => {
        this.pdpConfig[field] = data[field]
      })
    } else {
      this.pdpLoaded = false
    }

    this.addLensChoiceOverride()

    this.hydrated = true

    this.events = createApp()

    // Add any upsells if they're included in the response
    if (data.upsells) {
      this._upsells = data.upsells
        .map((upsell, order) => new Upsell({ ...upsell, order }))
        .filter((upsell) => upsell.isValidForProduct(this))
    }

    // Trick Vue into thinking this instance already has an observer to prevent it adding a new one
    this.__ob__ = reactive({}).__ob__
    if (this.baseProduct) this.baseProduct.__ob__ = reactive({}).__ob__
  }

  updatePrices() {
    this.basePrice = this._rawData.basePrice
    this.compareAtPrice = this._rawData.compare_at_amount_float
    this.currency = this._rawData.currency
  }

  addParts(getPart) {
    this.parts =
      this._rawData.parts?.map((part) => {
        return getPart(part.id)
      }) || []

    return this.parts
  }

  addLensChoiceOverride() {
    this.lensChoiceOverride = this._rawData.lensChoiceOverride
      ? new PartOption(this._rawData.lensChoiceOverride)
      : null
  }

  get formattedName() {
    return `Formatted name for: ${this.sku}.`
  }

  get allOptions() {
    if (this._allOptions) return this._allOptions
    this._allOptions = Object.fromEntries(
      this.parts
        .map((part) =>
          part.options.map((option) => {
            return [
              [option.sku, option],
              [part.skuPrefix + '_' + option.sku.split('_').slice(1).join('_'), option],
            ]
          })
        )
        .flat()
        .flat()
    )
    return this._allOptions
  }

  get allOptionsById() {
    if (this._allOptionsById) return this._allOptionsById
    this._allOptionsById = Object.fromEntries(
      this.parts.flatMap((part) => {
        return part.options.map((option) => {
          return [option.id, option]
        })
      })
    )
    return this._allOptionsById
  }

  get optionToPartMapping() {
    if (this._optionToPartMapping) return this._optionToPartMapping
    this._optionToPartMapping = Object.fromEntries(
      this.parts.flatMap((part) => {
        return part.options.flatMap((option) => {
          return [
            [option.sku, part],
            [part.skuPrefix + '_' + option.sku.split('_').slice(1).join('_'), part],
          ]
        })
      })
    )
    return this._optionToPartMapping
  }

  get pdpConfigWithDefaults() {
    const selectArray = (a, b, fallback = []) => {
      if (a?.length) return a
      if (b?.length) return b
      return fallback
    }

    let ctaSuffix = this.pdpConfig.ctaSuffix
    if (!ctaSuffix && this.pageMode === 'custom') {
      ctaSuffix = this.baseProduct?.pdpConfig?.ctaSuffix
    }

    // If the ctaSuffix is null and there is promoMessaging enabled then use it
    if (!ctaSuffix && this.promoMessaging?.enabled && this.promoMessaging?.ctaSuffix) {
      ctaSuffix = this.promoMessaging.ctaSuffix
    }

    return {
      theme: this.pdpConfig.theme || this.baseProduct?.pdpConfig?.theme,
      productInfoTitle: this.pdpConfig.productInfoTitle || this.baseProduct?.pdpConfig?.productInfoTitle,
      productInfoText: this.pdpConfig.productInfoText || this.baseProduct?.pdpConfig?.productInfoText,
      relatedProducts: this.relatedProducts || this.baseProduct?.relatedProducts || { products: [] },
      quotes: selectArray(this.pdpConfig.quotes, this.baseProduct?.pdpConfig?.quotes),
      quotesSectionTitle: this.pdpConfig.quotesSectionTitle,
      techSpecs: selectArray(this.pdpConfig.techSpecs, this.baseProduct?.pdpConfig?.techSpecs).map(
        (spec) => ({
          ...spec,
          icon: spec.linkedIcon?.icon || spec.icon,
        })
      ),
      designImagesOrder:
        this.pdpConfig.designImagesOrder || this.baseProduct?.pdpConfig?.designImagesOrder || [],
      inTheBoxItems: selectArray(
        this.pdpConfig.inTheBoxItems,
        this.baseProduct?.pdpConfig?.inTheBoxItems,
        []
      ),
      hideSwatches: this.pdpConfig.hideSwatches ?? this.baseProduct?.pdpConfig?.hideSwatches ?? false,
      stockIndicatorThreshold:
        this.pdpConfig.stockIndicatorThreshold ?? this.baseProduct?.pdpConfig?.stockIndicatorThreshold ?? 0,
      stockIndicatorThresholdText:
        this.pdpConfig.stockIndicatorThresholdText ??
        this.baseProduct?.pdpConfig?.stockIndicatorThresholdText,
      stockIndicatorSoldOutText:
        this.pdpConfig.stockIndicatorSoldOutText ?? this.baseProduct?.pdpConfig?.stockIndicatorSoldOutText,
      ctaSuffix,
      showDefaultInTheBoxProduct:
        this.pdpConfig.showDefaultInTheBoxProduct ??
        this.baseProduct?.pdpConfig?.showDefaultInTheBoxProduct ??
        true,
      staticProductImages: selectArray(
        this.pdpConfig.staticProductImages,
        this.baseProduct?.pdpConfig?.staticProductImages
      ),
    }
  }

  getOptionBySKU(optionSKU) {
    return this.allOptions[optionSKU]
  }

  getOptionById(optionId) {
    return this.allOptionsById[optionId]
  }

  getPartBySKU(optionSKU) {
    return this.optionToPartMapping[optionSKU]
  }

  get legacySKUMapping() {
    if (this._legacySKUMapping) return this._legacySKUMapping
    this._legacySKUMapping = Object.fromEntries(
      this.parts?.flatMap((part) => {
        const mapping = [[part.id, part.skuPrefix]]
        return mapping
      })
    )
    return this._legacySKUMapping
  }

  _sortChoices(choices) {
    // Choices should be sorted by the order of the parts as defined in Dato (Base Product > Parts) using the `stockManagedSKUPrefix` property.
    // This is the key used in the `choices` object.
    // Which has shape `{ [stockManagedSKUPrefix]: PartOption }`

    // Convert `choices` object into an array and filter out any pairs where the value is falsy.
    const filteredOptions = Object.entries(choices).filter(([_, option]) => option)

    // Sort the remaining options based by the index of the corresponding `part` object in the `this.parts` array.
    // The `stockManagedSKUPrefix` property of each `Part` object must match the key in the `choices`.
    const sortedOptions = filteredOptions.sort(([a], [b]) => {
      // Find the index of `a` and `b` in the `this.parts` array based on their `stockManagedSKUPrefix` property.
      const aIndex = this.parts.findIndex((part) => part.id === a)
      const bIndex = this.parts.findIndex((part) => part.id === b)
      return aIndex - bIndex
    })

    // Convert the sorted array back into an array of just the values and return it.
    return sortedOptions.reduce((previousValue, [_, option]) => {
      if (option) {
        return previousValue.concat(option)
      }

      return previousValue
    }, [])
  }

  _getSKUFromChoices(base, choices) {
    if (!base || !choices) return null

    const choiceSkus = this._sortChoices(choices || {}).map((choice) => choice.sku)

    return [base, ...choiceSkus].join('-')
  }

  _getSKUWithoutLensTech(base, choices) {
    if (!base || !choices) return null

    const choiceSkus = this._sortChoices(choices || {}).map((choice) => {
      if (choice.partType === 'lenses') {
        return getSkuWithoutLensTech(choice.sku)
      }

      return choice.sku
    })

    return [base, ...choiceSkus].join('-')
  }

  getSKUWithoutLensTech(base, choices) {
    return this._getSKUWithoutLensTech(base, choices)
  }

  _getLegacySKUFromChoices(base, choices) {
    if (!base || !choices) return null

    return [
      base,
      ...Object.entries(choices)
        .filter(([_, option]) => option)
        .map(([partId, option]) => {
          return option.getSKUWithPrefix(this.legacySKUMapping[partId])
        }),
    ].join('-')
  }

  _getNormalisedSKU(base, choices) {
    if (!base || !choices) return null

    const swappablePattern = new RegExp(
      this.swappableParts
        .flat()
        .map((prefix) => `${prefix}_`)
        .join('|')
    )

    // Iterate over choices
    const parts = Object.entries(choices)
      .map(([stockManagedSKUPrefix, option]) => {
        if (!option) return null

        // Get corresponding part
        const part = this.parts.find((part) => part.stockManagedSKUPrefix === stockManagedSKUPrefix)

        if (!part) return

        // If option is a string then find the choice instance
        if (typeof option === 'string') {
          option = part.options.find((opt) => opt.sku.endsWith(`_${option}`))
        }

        // If the part has excludeFromAdFeeds set to true, skips to the next choice (e.g. icons/logos)
        if (part?.excludeFromAdFeeds) return null

        // Ignore lens techs, returns the skuWithoutLensTech property of the option
        if (option?.partType === 'lenses') return option?.skuWithoutLensTech

        return option?.sku
      })
      .filter((option) => option)
      .map((optionSKU) => {
        if (this.swappableParts.length) {
          return optionSKU.replace(swappablePattern, 'swap_')
        } else {
          return optionSKU
        }
      })

    return [base, ...[...new Set(parts)].sort((a, b) => a.localeCompare(b))].join('-')
  }

  _getAdFeedSKU(base, choices) {
    if (!base || !choices) return null

    // If this product isn't part of an ad feed, return null
    if (!this.includeInProductFeed) return null

    const parts = this.parts
      .map((part) => {
        const option = choices[part.id]
        if (!option) return false
        if (part.excludeFromAdFeeds) return false

        return part.skuPrefix + '_' + option.sku.split('_').slice(1).join('_')
      })
      .filter((part) => part)

    return [base, ...parts].join('-')
  }

  buildAllHeroDesigns() {
    this._heroDesigns = this._buildHeroDesigns()
    this._eyeglassesHeroDesigns = this._buildHeroDesigns(TAG_EYEGLASSES)
    this._prescriptionHeroDesigns = this._buildHeroDesigns(TAG_PRESCRIPTION)
  }

  _buildHeroDesigns(productTypeFilter) {
    if (this.baseProduct) return // Don't build hero designs for products with a base product (eg limited editions)

    const datoHeroDesigns = this.productHeroDesigns || []
    let heroDesigns = datoHeroDesigns
    let additionalHeroDesigns = []

    if (this.shopifyPrebuiltProduct && this.prebuiltVariants?.length) {
      if (this.relatedProducts?.slug === 'goggles') additionalHeroDesigns = datoHeroDesigns

      heroDesigns = this.prebuiltVariants
        .filter((variant) => variant.display?.value !== 'silent')
        .map((variant) => ({
          ...variant,
          enabled: true,
          isPrebuilt: ![TAG_EYEGLASSES, TAG_PRESCRIPTION].includes(productTypeFilter),
          variant,
          ...(![TAG_EYEGLASSES, TAG_PRESCRIPTION].includes(productTypeFilter)
            ? { prebuiltSKU: variant.sku }
            : {}),
        }))
    }

    const customHeroDesigns = [
      ...this._processHeroDesigns(heroDesigns, productTypeFilter),
      ...this._processHeroDesigns(additionalHeroDesigns, productTypeFilter),
    ]

    const limitedEditions = this.limitedEditions

    const targetLength = 20
    return [...customHeroDesigns.slice(0, targetLength), ...limitedEditions]
  }

  _processHeroDesigns(heroDesigns, productTypeFilter) {
    return heroDesigns
      .filter(({ enabled }) => enabled)
      .map((design) => {
        // Get instances of each option in this design
        const choices = design.variant?.choices
          ? Object.fromEntries(
              Object.entries(design.variant.choices).map(([partId, choice]) => {
                if (!choice?.sku) return [partId, choice]

                let [optionSKUPrefix, optionSKUSuffix] = choice.sku.split('_')

                const part = this.parts.find((part) => {
                  return part.skuPrefix === optionSKUPrefix || part.stockManagedSKUPrefix === optionSKUPrefix
                })

                if (productTypeFilter === TAG_PRESCRIPTION && part?.type === 'lenses') {
                  optionSKUSuffix = optionSKUSuffix.replace(/(P|N|8P|8)/g, 'STRX')
                } else if (productTypeFilter === TAG_EYEGLASSES && part?.type === 'lenses') {
                  optionSKUSuffix = 'STCRXclear'
                }

                const updatedChoice = part?.options.find((option) =>
                  option.sku.endsWith(`_${optionSKUSuffix}`)
                )

                return [partId, updatedChoice || choice]
              })
            )
          : Object.fromEntries(
              design.parts
                .map(({ sku: optionSKU }) => {
                  let [optionSKUPrefix, optionSKUSuffix] = optionSKU.split('_')

                  const part = this.parts.find((part) => {
                    return (
                      part.skuPrefix === optionSKUPrefix || part.stockManagedSKUPrefix === optionSKUPrefix
                    )
                  })

                  if (productTypeFilter === TAG_PRESCRIPTION && part?.type === 'lenses') {
                    optionSKUSuffix = optionSKUSuffix.replace(/(P|N|8P|8)/g, 'STRX')
                  } else if (productTypeFilter === TAG_EYEGLASSES && part?.type === 'lenses') {
                    optionSKUSuffix = 'STCRXclear'
                  }

                  let choice = part?.options.find((option) => option.sku.endsWith(`_${optionSKUSuffix}`))

                  if (!choice) return null

                  /**
                   * Prefer non-infinite products until they sell out
                   */
                  if (choice.isRecycled) {
                    const nonInfAlternative = part.getNonInfiniteAlternative(choice)
                    if (nonInfAlternative && nonInfAlternative.isInStock) {
                      choice = nonInfAlternative
                    }
                  } else if (choice.isNonRecycled) {
                    if (!choice.isInStock) {
                      const infAlternative = part.getInfiniteAlternative(choice)
                      if (infAlternative && infAlternative.isInStock) {
                        choice = infAlternative
                      }
                    }
                  }

                  return [part.id, choice]
                })
                .filter((choice) => choice)
            )

        const options = Object.values(choices)

        if (options.some((option) => !option)) {
          // At least one option is missing, log and bail early
          const missingPart = Object.entries(choices).find(([_, option]) => !option)
          console.log(`Missing option in design ${design.sku}:`, missingPart[0])
          return
        }

        const frameOption = options.find((option) => option.partType === 'frame')
        const lensOption = options.find((option) => option.partType === 'lenses')

        const iconOption = options.find((option) => option.partType === 'icon')

        const normalisedSKU = this._getNormalisedSKU(this.sku, choices)
        const sku = productTypeFilter
          ? this._getSKUFromChoices(this.sku, choices)
          : (design.prebuiltSKU ?? this._getSKUFromChoices(this.sku, choices))
        const skuWithoutLensTech = this._getSKUWithoutLensTech(this.sku, choices)

        let swatches = [
          frameOption?.swatch,
          productTypeFilter !== TAG_EYEGLASSES && lensOption?.swatch ? lensOption.swatch : null,
          productTypeFilter === TAG_EYEGLASSES && iconOption?.swatch ? iconOption.swatch : null,
        ].filter((a) => a)
        if (design.swatchMode === 'frame' && frameOption?.swatch) {
          swatches = [frameOption.swatch]
        }

        // Availability is already scoped to the customer's eligible locations server-side
        // (see `locationScopedQuantity` in the product route), so the aggregate reads here
        // are the location-correct ones.
        let isInStock = options.every((option) => option.isInStock)
        let isOnPreorder = options.some((option) => option.isOnPreorder)
        let isInStockOrOnPreorder = options.some((option) => option.isInStock || option.isOnPreorder)
        let allExceptLensInStockOrOnPreorder = options
          .filter((option) => option.partType !== 'lenses')
          .every((option) => option.isInStock || option.isOnPreorder)

        if (design.isPrebuilt) {
          isInStock = design.variant?.isInStock
          isOnPreorder = design.variant?.isOnPreorder
          isInStockOrOnPreorder = isInStock || isOnPreorder

          const alternativeLensPrebuiltVariant = heroDesigns.find((otherDesign) => {
            if (otherDesign.sku === design.sku) return false
            const nonLensOptions = otherDesign.parts.filter((part) => part.partType !== 'lenses')
            return nonLensOptions.every((part) => {
              const otherChoice = otherDesign.choices[part.id]
              const choice = choices[part.id]
              return otherChoice?.sku === choice?.sku
            })
          })

          const alternativeInStock = alternativeLensPrebuiltVariant?.variant?.isInStock ?? false
          allExceptLensInStockOrOnPreorder = isInStock || alternativeInStock
        }

        return {
          ...design,
          baseSku: this.baseSKU,
          sku,
          skuWithoutLensTech,
          legacySKU: this._getLegacySKUFromChoices(this.sku, choices),
          normalisedSKU,
          options,
          frameOption,
          lensOption,
          swatches,
          price: this.basePrice + options.reduce((sum, option) => sum + option.price, 0),
          compareAtPrice:
            this.compareAtPrice + options.reduce((sum, option) => sum + option.compareAtPrice, 0),
          isInStock,
          isOnPreorder,
          isInStockOrOnPreorder,
          allExceptLensInStockOrOnPreorder,
          isRecycled: frameOption?.isRecycled,
          isIrisLens: lensOption?.isIrisLens,
          url: `${this.url}?sku=${sku}`,
        }
      })
      .filter((design) => design)
      .sort((a, b) => {
        return Number(b.isInStockOrOnPreorder) - Number(a.isInStockOrOnPreorder)
      })
      .filter((design, index, self) => {
        // Skip deduplication if either product has secondary parts
        if (design.secondaryParts?.length || self.secondaryParts?.length) {
          return true
        }

        return (
          self.findIndex((d) => {
            return d.skuWithoutLensTech === design.skuWithoutLensTech
          }) === index
        )
      })
  }

  get prescriptionHeroDesigns() {
    if (this.baseProduct) return this.baseProduct.prescriptionHeroDesigns

    if (!this._prescriptionHeroDesigns) {
      this._prescriptionHeroDesigns = this._buildHeroDesigns(TAG_PRESCRIPTION)
    }
    return this._prescriptionHeroDesigns
  }

  get eyeglassesHeroDesigns() {
    if (this.baseProduct) return this.baseProduct.eyeglassesHeroDesigns

    if (!this._eyeglassesHeroDesigns) {
      this._eyeglassesHeroDesigns = this._buildHeroDesigns(TAG_EYEGLASSES)
    }
    return this._eyeglassesHeroDesigns
  }

  get heroDesigns() {
    if (this.baseProduct) return this.baseProduct.heroDesigns

    if (!this._heroDesigns) {
      this._heroDesigns = this._buildHeroDesigns()
    }
    return this._heroDesigns
  }

  get limitedEditions() {
    const limitedEditionsData = this.pdpConfig?.limitedEditions || this._rawData.limitedEditions

    return (
      limitedEditionsData?.map((limitedEdition) => {
        return {
          sku: limitedEdition.customisedProduct.sku,
          legacySKU: limitedEdition.customisedProduct.sku,
          normalisedSKU: this._getNormalisedSKU(
            this.sku.split('-')[0],
            Object.fromEntries(
              limitedEdition.customisedProduct.sku
                .split('-')
                .slice(1)
                .map((skuPart) => skuPart.split('_'))
            )
          ),
          options: [],
          frameOption: null,
          lensOption: null,
          swatches: [null, null],
          swatchStyle: {
            cornerIcon:
              limitedEdition.customisedProduct?.baseProduct?.swatchCornerIcon ||
              limitedEdition.customisedProduct.swatchCornerIcon,
            cornerStyle:
              limitedEdition.customisedProduct?.baseProduct?.swatchCornerStyle ||
              limitedEdition.customisedProduct.swatchCornerStyle,
            cornerBackgroundImage:
              limitedEdition.customisedProduct?.baseProduct?.swatchIconOverride?.url ||
              limitedEdition.customisedProduct?.swatchIconOverride?.url,
          },
          swatchIcon:
            limitedEdition.customisedProduct?.baseProduct?.swatchIconOverride?.url ||
            limitedEdition.customisedProduct?.swatchIconOverride?.url,
          price: null,
          isInStock: true,
          isRecycled: false,
          isIrisLens: false,
          variantGroup: limitedEdition.customisedProduct?.variantGroup || null,
          isLimitedEdition: true,
          isPartBasedLimitedEdition:
            limitedEdition.customisedProduct?.partsLimitedEdition || limitedEdition.isCustomisable,
          url: limitedEdition.customisedProduct?.baseProduct?.url,
        }
      }) || []
    )
  }

  get baseSKU() {
    return this.baseProduct?.sku || this.sku
  }

  get allowLensTechFiltering() {
    if (this.configurableProductSelector === 'lens_tech') return true

    if (this.isLimitedEdition) return false

    return [
      'classics3',
      'classics4',
      'classics4pro',
      'sierras',
      'renegades',
      'tempests',
      'zephyrs',
      'vanguards',
      'ullrs',
      'snipers',
      'tokas',
      'miras',
    ].includes(this.baseSKU.replace('archive-', ''))
  }

  get allLensTechs() {
    const lensParts = this.parts.filter((part) => part.type === 'lenses')
    const lensTechs = {}
    lensParts.forEach((part) => {
      part.options.forEach((option) => {
        if (option.optionTech) {
          lensTechs[option.optionTech.tech] = option.optionTech
        }
      })
    })
    return Object.values(lensTechs)
  }

  get isLimitedEdition() {
    if (this.sku === 'egiftcard') return false
    if (this.sku.includes('-ct_')) return false
    return this.sku.includes('-le_')
  }

  get minimumPrice() {
    if (this.swappableParts.length) throw new Error('Cannot calculate minimumPrice for swappable products')
    return this.parts
      .map((part) => {
        return part.minimumPrice
      })
      .reduce((sum, val) => sum + val, 0)
  }

  get minimumCompareAtPrice() {
    if (this.swappableParts.length)
      throw new Error('Cannot calculate minimumCompareAtPrice for swappable products')
    return this.parts
      .map((part) => {
        return part.minimumCompareAtPrice
      })
      .reduce((sum, val) => sum + val, 0)
  }

  get isInStock() {
    return (this.doNotTrackStock || this.quantity > 0) && this.availableForSale !== false
  }

  get isOnPreorder() {
    return (
      !this.isInStock && this.inboundDeliveryDate && this.allowPreorders && !this.preorderAsDelayedDespatch
    )
  }

  get isDelayedDespatch() {
    if (this.despatchDelayedUntil) {
      // Return true if the delayed despatch date is in the future
      return dayjs().isBefore(dayjs(this.despatchDelayedUntil))
    }

    if (this.preorderAsDelayedDespatch) {
      return this.isOnPreorder
    }

    return false
  }

  get delayedDespatchDate() {
    return [this.despatchDelayedUntil, this.preorderAsDelayedDespatch && this.inboundDeliveryDate]
      .filter(Boolean)
      .map((date) => dayjs(date))
      .sort((a, b) => a.diff(b))[0]
  }

  get show360() {
    return !!this.layerTemplate
  }

  get layerTemplate() {
    return allLayerTemplates[this.sku.replace('archive-', '')]
  }

  get upsells() {
    const upsells = this._upsells || []

    // For limited editions we can also include upsells from the base product
    if (this.isLimitedEdition && this.baseProduct?.upsells?.length) {
      upsells.push(...this.baseProduct.upsells)
    }

    return upsells
  }

  get reviewSummary() {
    return this._allReferencingProductReviewAggregations
  }

  get tagTextWithOverride() {
    if (this.tagText) return this.tagText
    if (this.promoMessaging?.enabled && this.promoMessaging?.tagText) {
      return this.promoMessaging.tagText
    }
    return null
  }

  setPrebuiltVariants() {
    const currency = this.currency
    this.prebuiltVariants =
      this._rawData.prebuiltVariants
        ?.map((variant) => {
          const choices = Object.fromEntries(
            variant.prebuiltParts
              .map((shopifyPart) => {
                if (!this.getPartBySKU(shopifyPart.sku)) {
                  if (import.meta.dev) {
                    console.warn(
                      'Part ' +
                        shopifyPart.sku +
                        ' not found for prebuilt variant ' +
                        variant.sku +
                        '. Check the parts assigned to the prebuilt product match the parts available for the product.'
                    )
                    return
                  }
                }

                if (this.getPartBySKU(shopifyPart.sku)?.id) {
                  return [this.getPartBySKU(shopifyPart.sku).id, this.getOptionBySKU(shopifyPart.sku)]
                }
              })
              .filter(Boolean)
          )

          const secondaryChoices = Object.fromEntries(
            variant.prebuiltSecondaryParts?.map((shopifyPart) => {
              return [this.getPartBySKU(shopifyPart.sku)?.id, this.getOptionBySKU(shopifyPart.sku)]
            }) || []
          )

          const prebuiltVariant = {
            ...variant,
            customSKU: this._getSKUFromChoices(this.sku, choices),
            skuWithoutLensTech: this._getSKUWithoutLensTech(this.sku, choices),
            legacySKU: this._getLegacySKUFromChoices(this.sku, choices),
            normalisedSKU: this._getNormalisedSKU(this.sku, choices),
            parts: variant.prebuiltParts,
            secondaryParts: variant.prebuiltSecondaryParts,
            price: variant.prices?.[currency],
            compareAtPrice: variant.compare_at_prices?.[currency],
            variantGroup: variant.variantGroup?.value || null,
            choices,
            secondaryChoices,
          }
          delete prebuiltVariant.prebuiltParts
          delete prebuiltVariant.prebuiltSecondaryParts

          return prebuiltVariant
        })
        .filter((variant) => {
          // Check choices are set for every part
          const allChoicesSet = variant.parts.every((part) => {
            const choice = this.getOptionBySKU(part.sku)
            return choice && choice.sku === part.sku
          })
          // Check secondary choices are set for every part
          const allSecondaryChoicesSet =
            !(variant.secondaryParts || []).length ||
            variant.secondaryParts?.every((part) => {
              const choice = this.getOptionBySKU(part.sku)
              return choice && choice.sku === part.sku
            })

          if (!allChoicesSet || !allSecondaryChoicesSet) {
            console.log(
              `Prebuilt variant ${variant.sku} is missing choices for parts:`,
              variant.parts.filter((part) => {
                const choice = variant.choices[part.sku.split('_')[0]]
                return !choice || choice.sku !== part.sku
              })
            )
          }

          return allChoicesSet && allSecondaryChoicesSet
        }) || []
  }

  setProductVariants() {
    const currency = this.currency

    this.productVariants = this._rawData.productVariants?.map((variant) => {
      // Resolve choices for each variant if there are any
      const choices = Object.fromEntries(
        variant.parts.map((partOption) => {
          const { sku } = partOption
          const part = this.getPartBySKU(sku)
          const choice = this.getOptionBySKU(sku)
          return [part.id, choice]
        })
      )

      return {
        ...variant,
        sku: variant.shopifyProductVariant.sku,
        choices,
        price: variant.prices?.[currency],
        compareAtPrice: variant.compare_at_prices?.[currency],
      }
    })
  }

  get configurableProductSelector() {
    return this._rawData.configurableProduct?.variantSelector
  }

  setConfigurableProductVariants() {
    if (!this._rawData.configurableProduct?.variants?.length) return

    const allLensTechs = this.baseProduct?.allLensTechs

    const currency = this.currency

    this.configurableProductVariants = this._rawData.configurableProduct.variants.map((variant) => {
      const optionTech = allLensTechs?.find(({ tech }) => tech === variant.lensTechnology?.value)

      return {
        ...variant,
        optionTech,
        price: variant.prices?.[currency],
        compareAtPrice: variant.compare_at_prices?.[currency],
      }
    })
  }

  updateFromShopifyResponse(customisedProduct, freshStockResponse) {
    const freshStockQuatitiesPerSku = getFreshStockQuantities(customisedProduct, freshStockResponse)

    if (this.oosAsNotInvited) {
      return // Don't update if the user isn't invited as we've overridden stock here
    }

    const product = freshStockQuatitiesPerSku.find((product) => product.sku === this.sku)

    if (!product) return

    this.quantity = product.quantity
  }

  toJSON() {
    return {
      pdpLoaded: this.pdpLoaded,
      _rawData: this._rawData,
    }
  }

  static fromJSON(data, getPart) {
    const product = new Product(data._rawData, data.pdpLoaded)
    product.addParts(getPart)
    product.setPrebuiltVariants()
    product.buildAllHeroDesigns()

    if (data._rawData.baseProduct) {
      product.baseProduct = Product.fromJSON(
        {
          _rawData: data._rawData.baseProduct,
          pdpLoaded: data.pdpLoaded,
        },
        getPart
      )
    }

    product.setProductVariants()
    product.setConfigurableProductVariants()

    return product
  }
}
